// DataTableGrid utils.
import type { GridColDef } from '@mui/x-data-grid-pro';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';

import IconCell from './IconCell';
import TitleLinkCell from './TitleLinkCell';
import {
  TDataTableGridIcons,
  TDataTableGridLegend,
  TWidgetTableColumn,
} from './constants';
import {
  formatChartValue,
  getChartConfig,
  normalizeModuleParameters,
} from 'components/Dashboard/shared/utils';
import type { TWidget, TWidgetData } from 'utility/types/dataProvider';
import type { DynamicFieldType } from 'components/DynamicFormField/DynamicFormField';

// Tree data utility types
export type TTreeDataRow = Record<string, any> & {
  path: string[];
  id: string;
};

/**
 * Detects the nested data key from columns with dot notation.
 * E.g., from ['purchase_event', 'user_data.device', 'user_data.users']
 * returns 'user_data'
 */
export const getTreeDataKey = (columns: string[]): string | null => {
  const nestedColumns = columns.filter((col) => col.includes('.'));
  if (nestedColumns.length === 0) return null;

  // Extract the parent key (everything before the first dot)
  const parentKey = nestedColumns[0].split('.')[0];
  return parentKey;
};

/**
 * Extracts value from a nested path in dot notation.
 * E.g., getValue(row, 'user_data.device') gets row.device when row is a child
 */
export const getNestedValue = (
  row: any,
  field: string,
  isChildRow: boolean = false,
): any => {
  // If it's a child row, remove the parent prefix (e.g., 'user_data.device' -> 'device')
  if (isChildRow && field.includes('.')) {
    const childField = field.split('.').slice(1).join('.');
    return row[childField];
  }

  // For parent rows, try to get the value directly
  return row[field];
};

/**
 * Transforms nested tree data into a flat array with path information for MUI DataGridPro.
 * Converts data like:
 * [{
 *   purchase_event: 'Product List Viewed',
 *   users: 4008,
 *   user_data: [
 *     { device: 'desktop', users: 3423, ... },
 *     { device: 'mobile', users: 585, ... }
 *   ]
 * }]
 *
 * Into flat structure:
 * [
 *   { id: '0', path: ['Product List Viewed'], purchase_event: 'Product List Viewed', users: 4008, ... },
 *   { id: '0-0', path: ['Product List Viewed', 'desktop'], device: 'desktop', users: 3423, ... },
 *   { id: '0-1', path: ['Product List Viewed', 'mobile'], device: 'mobile', users: 585, ... }
 * ]
 */
export const flattenTreeData = (
  data: TWidgetData['results'],
  treeDataKey: string,
  parentIdentifier: string,
): TTreeDataRow[] => {
  if (!Array.isArray(data) || !treeDataKey) {
    console.error('Invalid data or treeDataKey for flattenTreeData');
    return [];
  }

  const flatRows: TTreeDataRow[] = [];

  data.forEach((parentRow, parentIndex) => {
    if (!parentRow || typeof parentRow !== 'object') {
      console.warn(`Skipping invalid parent row at index ${parentIndex}`);
      return;
    }

    const parentId: string = parentRow.id ?? `${parentIndex}`;
    const parentPathValue = String(
      parentRow[parentIdentifier] ?? `Parent ${parentIndex}`,
    );

    // Add parent row with its path
    const parentFlatRow: TTreeDataRow = {
      ...parentRow,
      id: parentId,
      path: [parentPathValue],
      _isParentRow: true,
    };

    // Remove the nested array from the parent row to avoid confusion
    delete parentFlatRow[treeDataKey];

    // Add child rows with their paths
    const childRows = parentRow[treeDataKey];
    if (Array.isArray(childRows)) {
      // First, find the "total" row if it exists
      const totalRow = childRows.find((childRow) => {
        if (!childRow || typeof childRow !== 'object') return false;
        const identifier = String(
          childRow.title ?? childRow.device ?? childRow.name ?? '',
        ).toLowerCase();
        return identifier === 'total';
      });

      // If a "total" row exists, merge its data into the parent row
      if (totalRow) {
        // Merge all fields from the total row except structural fields
        Object.keys(totalRow).forEach((key) => {
          // Don't overwrite the parent's identifier, id, or path
          // Allow 'device' to be merged so "total" can be displayed
          if (
            key !== parentIdentifier &&
            key !== 'id' &&
            key !== 'title' &&
            key !== 'name'
          ) {
            parentFlatRow[key] = totalRow[key];
          }
        });
        // Mark that this parent has total data merged
        parentFlatRow._hasTotalData = true;
      }

      flatRows.push(parentFlatRow);

      childRows.forEach((childRow, childIndex) => {
        if (!childRow || typeof childRow !== 'object') {
          console.warn('Skipping invalid child row:', {
            parentIndex,
            childIndex,
            childRow,
          });
          return;
        }

        // Use a field that uniquely identifies the child (e.g., 'device')
        // If not available, use the index
        const childIdentifier = String(
          childRow.title ?? childRow.device ?? childRow.name ?? `Child ${childIndex}`,
        );

        // Skip the "total" row - it's already merged into the parent
        if (childIdentifier.toLowerCase() === 'total') {
          return;
        }

        const childId = `${parentId}-${childIndex}`;

        const childFlatRow: TTreeDataRow = {
          ...childRow,
          id: childId,
          path: [parentPathValue, childIdentifier],
          _isChildRow: true,
          _parentId: parentId,
        };

        flatRows.push(childFlatRow);
      });
    } else {
      // No child rows, just add the parent
      flatRows.push(parentFlatRow);
    }
  });

  return flatRows;
};

export const getSubscriberPercent = (value: number, row: any) => {
  const percentFormatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
  });
  const percentage = value < 1 ? 0 : (row?.subscribers / value).toFixed(2);
  return percentFormatter.format(Number(percentage));
};

export const getCellIcon = (iconKey?: string): JSX.Element => {
  if (!iconKey) return <></>;

  const knownIcons: { [key: string]: JSX.Element } = {
    TrendingUp: <TrendingUpRoundedIcon />,
    KeyboardDoubleArrowDown: <KeyboardDoubleArrowDownRoundedIcon />,
    LocalFireDepartment: <LocalFireDepartmentRoundedIcon />,
    normal: <></>,
  };

  return knownIcons[iconKey] ?? knownIcons.normal;
};

// Form the columns for data grid widgets.
type TNxtGridColDef = GridColDef & {
  format?: string;
};

export type TGetTableWidgetConfig = {
  columns: TNxtGridColDef[];
  icons?: TDataTableGridIcons | undefined;
  legend?: TDataTableGridLegend | undefined;
  initHiddenColumns: Record<string, false> | undefined;
  isTree: boolean;
  treeDataKey?: string | null;
  groupingField?: { label: string; field: string } | null;
};

// Determine if a column is sortable based on widget.module.parameters (widget filter fields).
export const getIsColumnSortable = ({
  col,
  widget,
}: {
  col: TNxtGridColDef;
  widget: TWidget;
}) => {
  const normalizedModuleParameters = normalizeModuleParameters(widget);

  if (!normalizedModuleParameters?.length) return false;

  // If parameters is an array, find the "sort" parameter.
  const sortParamConfig = normalizedModuleParameters?.find((config: DynamicFieldType) => {
    return config?.name === 'sort';
  });

  // Check if column.field matches one of the parameter names in sortParamConfig.
  if (sortParamConfig) {
    const sortableColumns: string[] =
      sortParamConfig?.config?.options?.map(
        (opt: { label: string; value: string }) => opt.value,
      ) || [];

    return sortableColumns?.includes(col.field);
  }

  // No sort parameter declared by the API — sorting is not supported.
  return false;
};

export const getDataTableGridConfig = (widget: TWidget): TGetTableWidgetConfig => {
  // No data, no table.
  if (!widget?.module?.data) {
    return {
      columns: [],
      icons: undefined,
      legend: undefined,
      initHiddenColumns: undefined,
      isTree: false,
      treeDataKey: null,
      groupingField: null,
    };
  }

  // Table configuration.
  const { config: nxtTableConfig } = getChartConfig(widget);

  const isTree = nxtTableConfig?.subtype === 'tree';

  // Column with IconCell reference key (there can be only one ⚔️).
  let iconColumnKeyRef: string | null = null;

  // Init legend and icons.
  let legend: TDataTableGridLegend | undefined = undefined;
  let icons: TDataTableGridIcons | undefined = undefined;

  // Get columns from module.
  const allPossibleColumns = widget?.module?.data;
  const columnsToRender: string[] = nxtTableConfig?.columns ?? [];

  // Handle tree data columns
  let treeDataKey: string | null = null;
  let treeColumns: string[] = columnsToRender;
  let groupingField: { label: string; field: string } | null = null;

  if (isTree) {
    // Detect the nested data key (e.g., 'user_data' from 'user_data.device')
    treeDataKey = getTreeDataKey(columnsToRender);

    // Get the first non-nested column as the grouping field
    const parentColumn = columnsToRender.find((col) => !col.includes('.'));
    if (parentColumn && allPossibleColumns[parentColumn]) {
      groupingField = {
        label: allPossibleColumns[parentColumn].label,
        field: parentColumn,
      };

      // Filter out the grouping field from regular columns since MUI creates it automatically
      treeColumns = columnsToRender.filter((col) => col !== parentColumn);
    } else {
      treeColumns = columnsToRender;
    }
  }

  // These are columns that exist in the data but are not listed in the columnsToRender array.
  // We use this to set the initialState of the DataGridPro to hide these columns.
  // This way, users can still enable them via the column selector in the toolbar.
  // If we filter them out here, they won't be available in the column selector.
  const initHiddenColumns: TGetTableWidgetConfig['initHiddenColumns'] = isTree
    ? {} // For tree data, only show configured columns
    : Object.keys(allPossibleColumns).reduce<Record<string, false>>((acc, col) => {
        if (!columnsToRender.includes(col)) acc[col] = false;
        return acc;
      }, {});

  // Construct all the columns - takes the column configuration into account.
  const allColumns: GridColDef[] = (
    isTree ? treeColumns : Object.keys(allPossibleColumns)
  )
    .map((fieldKey: string) => {
      // For tree data with dot notation (e.g., 'user_data.device')
      const isNestedField = fieldKey.includes('.');
      const childField = isNestedField ? fieldKey.split('.').slice(1).join('.') : '';

      // Get the column config from the data
      // For nested fields, try to get config from the parent or create a default one
      let nxtColumnConfig: TWidgetTableColumn;

      if (isTree && isNestedField) {
        // For nested tree fields, we need to look in the nested data structure
        // The config might be in allPossibleColumns[treeDataKey][childField]
        // Or we create a sensible default
        const parentKey = fieldKey.split('.')[0];
        const nestedData = allPossibleColumns[parentKey];
        const nestedConfig =
          nestedData && typeof nestedData === 'object' && childField in nestedData
            ? (nestedData as any)[childField]
            : null;

        nxtColumnConfig = nestedConfig || {
          type: 'string',
          label:
            childField.charAt(0).toUpperCase() + childField.slice(1).replace(/_/g, ' '),
        };
      } else {
        nxtColumnConfig = allPossibleColumns[fieldKey];
      }

      if (!nxtColumnConfig) {
        console.warn(`No column config found for field: ${fieldKey}`);
        return null;
      }

      // Init column settings.
      const currentColumn: GridColDef = {
        field: fieldKey,
        headerName: nxtColumnConfig.label,
        type: nxtColumnConfig.type as GridColDef['type'],
        align: 'center',
        headerAlign: 'center',
        hideable: true,
        flex: 1,
        sortable: getIsColumnSortable({
          col: { field: fieldKey, ...nxtColumnConfig } as TNxtGridColDef,
          widget,
        }),
        // Custom value getter for tree data
        ...(isTree
          ? {
              valueGetter: (value: any, row: any) => {
                // Check if this is a child row
                const isChildRow = row._isChildRow === true;

                // For nested fields (e.g., 'user_data.device')
                if (isNestedField) {
                  if (isChildRow) {
                    // For child rows, get the value from the child field directly
                    return row[childField];
                  }
                  // For parent rows with merged total data, return the value from the child field
                  if (row._hasTotalData) {
                    return row[childField];
                  }
                  // For parent rows without total data, return undefined
                  return undefined;
                }

                // For parent fields on child rows, return undefined
                if (isChildRow && !isNestedField) {
                  return undefined;
                }

                // For parent fields on parent rows, return the value
                return value;
              },
            }
          : {}),
        valueFormatter: (value: any) => {
          // Don't format undefined/null values
          if (value === undefined || value === null) return '';

          // Auto-detect ISO datetime strings and format them
          if (
            typeof value === 'string' &&
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
          ) {
            return formatChartValue({
              value,
              format: nxtColumnConfig?.format || 'date',
              dateFormat: nxtColumnConfig?.dateFormat || 'M j, Y g:i A',
              precision: nxtColumnConfig?.precision,
            });
          }
          return formatChartValue({
            value,
            format: nxtColumnConfig?.format,
            precision: nxtColumnConfig?.precision,
          });
        },
      };

      // Icon column.
      if ('icons' in nxtColumnConfig) {
        // Output icon data.
        iconColumnKeyRef = fieldKey;
        icons = nxtColumnConfig.icons;

        // Output legend data.
        if ('legend' in nxtColumnConfig) {
          legend = nxtColumnConfig.legend;
        }

        // Set icon column settings.
        currentColumn.headerName = '';
        currentColumn.width = 54;
        currentColumn.flex = 0;
        currentColumn.align = 'center';
        currentColumn.headerAlign = 'center';
        currentColumn.cellClassName = 'cell-trend';
        currentColumn.renderCell = (row: any) => (
          <IconCell
            row={row}
            icons={icons}
          />
        );
      }

      // Link column.
      if ('link' in nxtColumnConfig) {
        currentColumn.align = 'left';
        currentColumn.headerAlign = 'left';
        currentColumn.flex = 4;
        currentColumn.renderCell = (row: any) => (
          <TitleLinkCell
            row={row}
            columnConfig={nxtColumnConfig}
            iconColumnKeyRef={iconColumnKeyRef}
            legendConfig={legend}
            iconsConfig={icons}
          />
        );
      }

      return currentColumn;
    })
    .filter((col): col is GridColDef => col !== null); // Filter out null columns

  // Sort columns according to the order specified in columnsToRender.
  const sortedColumns = allColumns.sort((a, b) => {
    const indexA = columnsToRender.indexOf(a.field);
    const indexB = columnsToRender.indexOf(b.field);

    // If both columns are in columnsToRender, sort by their order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only A is in columnsToRender, it comes first
    if (indexA !== -1) return -1;

    // If only B is in columnsToRender, it comes first
    if (indexB !== -1) return 1;

    // If neither is in columnsToRender, maintain original order
    return 0;
  });

  return {
    columns: sortedColumns,
    icons,
    legend,
    initHiddenColumns,
    isTree,
    treeDataKey,
    groupingField,
  };
};
